import {
  Beef,
  Beer,
  CakeSlice,
  Coffee,
  CupSoda,
  Fish,
  IceCream2,
  Pizza,
  Salad,
  Sandwich,
  Soup,
  Utensils,
  UtensilsCrossed,
  Wheat,
} from 'lucide-react'

/**
 * The icon a category carries — `FRONTEND.md` §9.91.
 *
 * The owner: *"we have in categories like ikonka kategórie, edit this… preset from the things from
 * the obedové menu… remove emojis, keep the one from obedové menu and replace them with these."*
 *
 * **The names are the daily menu's own section types** — `soup`, `pizza`, `burger`, `bowl`, `salad`,
 * `pasta`, `dessert` — plus the handful a drinks or party category needs. Sharing the vocabulary is
 * the point: a franchise that files a category under *soup* gets the same glyph the daily menu draws
 * over its soups.
 */
export const CATEGORY_ICONS = {
  soup: Soup,
  pizza: Pizza,
  burger: Sandwich,
  bowl: Utensils,
  salad: Salad,
  pasta: Wheat,
  meat: Beef,
  fish: Fish,
  dessert: CakeSlice,
  'ice-cream': IceCream2,
  drink: CupSoda,
  coffee: Coffee,
  beer: Beer,
  other: UtensilsCrossed,
} as const

export type CategoryIconName = keyof typeof CATEGORY_ICONS

export function isCategoryIconName(value: string | null | undefined): value is CategoryIconName {
  return typeof value === 'string' && value.trim() in CATEGORY_ICONS
}

/**
 * Headings to glyphs, by the start of the word — a franchise writes *Cestoviny - Gnocchi* and
 * *Cestoviny - Bolonské* for one dish, and *Dezert* or *Dezerty* for another.
 *
 * Longest first, so `polievka` is not read as `pizza` by a shorter stem that happens to precede it.
 */
const HEADINGS: ReadonlyArray<readonly [string, CategoryIconName]> = [
  ['polievk', 'soup'],
  ['cestovin', 'pasta'],
  ['gnocchi', 'pasta'],
  ['bolon', 'pasta'],
  ['dezert', 'dessert'],
  ['dessert', 'dessert'],
  ['zmrzlin', 'ice-cream'],
  ['burger', 'burger'],
  ['burgre', 'burger'],
  ['pizz', 'pizza'],
  ['salat', 'salad'],
  ['salad', 'salad'],
  ['bowl', 'bowl'],
  ['soup', 'soup'],
  ['napoj', 'drink'],
  ['kava', 'coffee'],
  ['pivo', 'beer'],
]

/**
 * The glyph a word names, or **null** where the set does not know the word.
 *
 * Its two callers want opposite defaults, which is why this answers null rather than choosing one: a
 * daily-menu section always draws something and falls back to the crossed forks, while a category
 * draws **nothing** when it has no icon — inventing one for all eighteen would be a change nobody
 * asked for.
 */
export function iconNameForHeading(label: string | null | undefined): CategoryIconName | null {
  const heading = normaliseHeading(label)
  if (heading === '') return null
  for (const [stem, icon] of HEADINGS) if (heading.startsWith(stem)) return icon
  return null
}

/** Case and diacritics are not a difference in a heading: *Šaláty*, *salaty* and *SALATY* are one. */
export function normaliseHeading(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLocaleLowerCase('sk')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/**
 * Draws a category's icon.
 *
 * **An emoji is replaced by the glyph the category's own name asks for**, which is the owner's
 * *"remove emojis"* without touching a populated column. Their two are `☕` on **Polievky** and `🍟`
 * on **Pizza** — a coffee cup on the soups and chips on the pizzas — and both names are words the set
 * knows, so both draw properly with nothing saved and nothing lost. Typing a real icon name still
 * overrides, and the emoji is still in the column if they want it back.
 *
 * **Anything the name cannot explain is still printed as it stands.** A category called *Špeciality*
 * carrying `🎉` keeps it: the set has no word for that, and dropping it would lose an icon somebody
 * chose on purpose.
 */
export function CategoryIcon({
  icon,
  name,
  className,
}: {
  icon: string | null | undefined
  /** The category's own name — read only when `icon` is not a name from the set. */
  name?: string | null
  className?: string
}) {
  const value = (icon ?? '').trim()
  if (value === '') return null
  if (!isCategoryIconName(value)) {
    const fromName = iconNameForHeading(name)
    if (fromName !== null) {
      const Named = CATEGORY_ICONS[fromName]
      return <Named className={className} aria-hidden />
    }
    // An emoji the name cannot explain, or anything else typed before §9.91 named the set.
    return <span className={className}>{value}</span>
  }
  const Glyph = CATEGORY_ICONS[value]
  return <Glyph className={className} aria-hidden />
}
